import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Bell, Send, CheckCircle, AlertCircle, Calendar, User, Phone, Loader2 } from 'lucide-react';
import { format, differenceInDays, differenceInHours } from 'date-fns';
import { tr } from 'date-fns/locale';

interface Deadline {
    id: string;
    title: string;
    type: 'exam' | 'application';
    due_date: string;
    student_id: string;
    student_name: string;
    phone: string;
    days_remaining: number;
    hours_remaining: number;
}

interface SmsReminderModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SmsReminderModal: React.FC<SmsReminderModalProps> = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [deadlines, setDeadlines] = useState<Deadline[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sendResults, setSendResults] = useState<{ success: number; failed: number } | null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchUpcomingDeadlines();
            setSelectedIds(new Set());
            setSendResults(null);
        }
    }, [isOpen]);

    const fetchUpcomingDeadlines = async () => {
        setLoading(true);
        try {
            const now = new Date();
            const threeDaysLater = new Date();
            threeDaysLater.setDate(threeDaysLater.getDate() + 3);

            // Fetch deadlines within 3 days
            const { data: deadlinesData, error } = await supabase
                .from('student_exam_deadlines')
                .select('*')
                .gte('due_date', now.toISOString())
                .lte('due_date', threeDaysLater.toISOString())
                .order('due_date', { ascending: true });

            if (error) throw error;

            if (!deadlinesData || deadlinesData.length === 0) {
                setDeadlines([]);
                setLoading(false);
                return;
            }

            // Get student info for each deadline
            const studentIds = [...new Set(deadlinesData.map(d => d.student_id))];

            // Fetch from both students and operation_students
            const [studentsResult, opStudentsResult] = await Promise.all([
                supabase.from('students').select('id, first_name, last_name, phone').in('id', studentIds),
                supabase.from('operation_students').select('id, first_name, last_name, phone').in('id', studentIds)
            ]);

            const studentMap = new Map<string, { name: string; phone: string }>();

            (studentsResult.data || []).forEach(s => {
                studentMap.set(s.id, {
                    name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                    phone: s.phone || ''
                });
            });

            (opStudentsResult.data || []).forEach(s => {
                studentMap.set(s.id, {
                    name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                    phone: s.phone || ''
                });
            });

            const enrichedDeadlines: Deadline[] = deadlinesData.map(d => {
                const studentInfo = studentMap.get(d.student_id) || { name: 'Bilinmiyor', phone: '' };
                const dueDate = new Date(d.due_date);
                return {
                    id: d.id,
                    title: d.title,
                    type: d.type,
                    due_date: d.due_date,
                    student_id: d.student_id,
                    student_name: studentInfo.name,
                    phone: studentInfo.phone,
                    days_remaining: differenceInDays(dueDate, now),
                    hours_remaining: differenceInHours(dueDate, now)
                };
            });

            setDeadlines(enrichedDeadlines);
        } catch (error) {
            console.error('Error fetching deadlines:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (id: string) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const selectAll = () => {
        const selectableIds = deadlines.filter(d => d.phone).map(d => d.id);
        if (selectedIds.size === selectableIds.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableIds));
        }
    };

    const handleSendSms = async () => {
        if (selectedIds.size === 0) return;

        setSending(true);
        setSendResults(null);

        let success = 0;
        let failed = 0;
        const successfulIds: string[] = [];

        try {
            const { data: userData } = await supabase.auth.getUser();
            const userId = userData?.user?.id || '';

            for (const id of selectedIds) {
                const deadline = deadlines.find(d => d.id === id);
                if (!deadline || !deadline.phone) {
                    failed++;
                    continue;
                }

                const message = deadline.type === 'exam'
                    ? `PISA Hatırlatma: ${deadline.student_name}, "${deadline.title}" sınavınıza ${deadline.hours_remaining < 24 ? deadline.hours_remaining + ' saat' : deadline.days_remaining + ' gün'} kaldı. Başarılar dileriz!`
                    : `PISA Bilgilendirme: ${deadline.student_name}, "${deadline.title}" başvurusu için son ${deadline.hours_remaining < 24 ? deadline.hours_remaining + ' saat' : deadline.days_remaining + ' gün'} kaldı. Lütfen eksikleri tamamlayınız.`;

                try {
                    const { error } = await supabase.functions.invoke('send-sms', {
                        body: {
                            phones: [deadline.phone],
                            message: message,
                            userId: userId,
                            branchId: ''
                        }
                    });

                    if (error) {
                        console.error('SMS Error:', error);
                        failed++;
                    } else {
                        success++;
                        successfulIds.push(id);
                    }
                } catch (err) {
                    console.error('SMS Send Error:', err);
                    failed++;
                }
            }

            setSendResults({ success, failed });
            setSelectedIds(new Set());

            // Remove successfully sent items from the list
            if (successfulIds.length > 0) {
                setDeadlines(prev => prev.filter(d => !successfulIds.includes(d.id)));
            }
        } catch (error) {
            console.error('Send SMS Error:', error);
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    const selectableCount = deadlines.filter(d => d.phone).length;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-orange-500 to-rose-500">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-xl">
                            <Bell className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">SMS Hatırlatıcı</h2>
                            <p className="text-sm text-white/80">Son 3 günü kalan sınav ve başvurular</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                            <span className="ml-3 text-gray-500">Yükleniyor...</span>
                        </div>
                    ) : deadlines.length === 0 ? (
                        <div className="text-center py-12">
                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Tebrikler! 🎉
                            </h3>
                            <p className="text-gray-500">
                                Son 3 gün içinde yaklaşan sınav veya başvuru bulunmuyor.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Results Banner */}
                            {sendResults && (
                                <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${sendResults.failed === 0
                                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                    : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                                    }`}>
                                    {sendResults.failed === 0 ? (
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                                    )}
                                    <span className="text-sm font-medium">
                                        {sendResults.success} SMS başarıyla gönderildi
                                        {sendResults.failed > 0 && `, ${sendResults.failed} başarısız`}
                                    </span>
                                </div>
                            )}

                            {/* Select All */}
                            <div className="flex items-center justify-between mb-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === selectableCount && selectableCount > 0}
                                        onChange={selectAll}
                                        disabled={selectableCount === 0}
                                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Tümünü Seç ({selectableCount} telefonu olan)
                                    </span>
                                </label>
                                <span className="text-sm text-gray-500">
                                    {selectedIds.size} seçili
                                </span>
                            </div>

                            {/* Deadline List */}
                            <div className="space-y-3">
                                {deadlines.map(deadline => (
                                    <div
                                        key={deadline.id}
                                        className={`p-4 rounded-xl border transition-all ${selectedIds.has(deadline.id)
                                            ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700'
                                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'
                                            } ${!deadline.phone ? 'opacity-50' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(deadline.id)}
                                                onChange={() => toggleSelect(deadline.id)}
                                                disabled={!deadline.phone}
                                                className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-bold text-gray-900 dark:text-white">
                                                        {deadline.title}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${deadline.type === 'exam'
                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                        }`}>
                                                        {deadline.type === 'exam' ? 'SINAV' : 'BAŞVURU'}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                                    <div className="flex items-center gap-1">
                                                        <User className="w-4 h-4" />
                                                        {deadline.student_name}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4" />
                                                        {format(new Date(deadline.due_date), 'dd MMM yyyy HH:mm', { locale: tr })}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Phone className="w-4 h-4" />
                                                        {deadline.phone || <span className="text-red-500 italic">Telefon yok!</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`px-3 py-1 rounded-full text-xs font-bold ${deadline.hours_remaining < 24
                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                : deadline.days_remaining <= 1
                                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                }`}>
                                                {deadline.hours_remaining < 24
                                                    ? `${deadline.hours_remaining} saat`
                                                    : `${deadline.days_remaining} gün`}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {deadlines.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                            Toplam {deadlines.length} yaklaşan tarih
                        </span>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                            >
                                Kapat
                            </button>
                            <button
                                onClick={handleSendSms}
                                disabled={selectedIds.size === 0 || sending}
                                className="flex items-center gap-2 px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Gönderiliyor...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Seçilenlere SMS Gönder ({selectedIds.size})
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SmsReminderModal;
